import { Injectable, inject } from '@angular/core';
import { Auth, authState, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, User as FirebaseUser, updateProfile, signInWithPopup, GoogleAuthProvider, getAdditionalUserInfo } from '@angular/fire/auth';
import { Observable, from, throwError, BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap, switchMap } from 'rxjs/operators';
import { User, UserCredentials, UserRegistration, FirebaseUserData, CreateUserDto } from '../models/user.interface';

/**
 * AuthService - Gestisce tutta la logica di autenticazione con Firebase
 * Lo store NgRx delegherà a questo servizio per tutte le operazioni auth
 */
@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private auth = inject(Auth);
  
  // Observable dello stato auth di Firebase (gestito da AngularFire)
  readonly authState$ = authState(this.auth);
  
  // Observable dei dati Firebase dell'utente corrente
  readonly firebaseUser$: Observable<FirebaseUserData | null> = this.authState$.pipe(
    map(firebaseUser => this.mapFirebaseUserToFirebaseUserData(firebaseUser))
  );
  
  // Observable dell'utente corrente (null fino a quando i dati backend non sono caricati)
  // Questo observable viene gestito dallo store che combina Firebase + Backend
  readonly user$: Observable<User | null> = of(null);

  // Subject per gestire gli errori
  private errorSubject = new BehaviorSubject<string | null>(null);
  readonly error$ = this.errorSubject.asObservable();

  /**
   * Login con Google
   * Apre un popup per l'autenticazione Google
   * Ritorna sia i dati utente che l'informazione se è un nuovo utente
   */
  loginWithGoogle(): Observable<{ firebaseUser: FirebaseUserData; isNewUser: boolean }> {
    const provider = new GoogleAuthProvider();
    // Opzionale: personalizza il provider
    provider.setCustomParameters({
      prompt: 'select_account' // Forza la selezione dell'account
    });

    return from(signInWithPopup(this.auth, provider)).pipe(
      map(userCredential => {
        const firebaseData = this.mapFirebaseUserToFirebaseUserData(userCredential.user);
        if (!firebaseData) {
          throw new Error('Errore nel recupero dati utente');
        }

        // Ottiene informazioni aggiuntive per capire se è un nuovo utente
        const additionalInfo = getAdditionalUserInfo(userCredential);
        const isNewUser = additionalInfo?.isNewUser ?? false;

        console.log(`🔐 Login Google: ${isNewUser ? 'NUOVO UTENTE' : 'UTENTE ESISTENTE'}`, {
          uid: firebaseData.uid,
          email: firebaseData.email,
          displayName: firebaseData.displayName
        });

        return { firebaseUser: firebaseData, isNewUser };
      }),
      catchError(error => {
        const errorMessage = this.getErrorMessage(error);
        this.errorSubject.next(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Login con email e password
   * Restituisce solo i dati Firebase, i dati backend verranno caricati successivamente
   */
  login(credentials: UserCredentials): Observable<FirebaseUserData> {
    return from(
      signInWithEmailAndPassword(this.auth, credentials.email, credentials.password)
    ).pipe(
      map(userCredential => {
        const firebaseData = this.mapFirebaseUserToFirebaseUserData(userCredential.user);
        if (!firebaseData) {
          throw new Error('Errore nel recupero dati utente');
        }
        return firebaseData;
      }),
      catchError(error => {
        const errorMessage = this.getErrorMessage(error);
        this.errorSubject.next(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Registrazione nuovo utente
   * Restituisce solo i dati Firebase, i dati backend verranno caricati successivamente
   */
  register(registration: UserRegistration): Observable<FirebaseUserData> {
    if (registration.password !== registration.confirmPassword) {
      const error = 'Le password non corrispondono';
      this.errorSubject.next(error);
      return throwError(() => new Error(error));
    }

    return from(
      createUserWithEmailAndPassword(this.auth, registration.email, registration.password)
    ).pipe(
      switchMap(userCredential => {
        // Aggiorna il profilo con nome e cognome
        const displayName = `${registration.name} ${registration.surname}`;
        return from(updateProfile(userCredential.user, { displayName })).pipe(
          map(() => userCredential.user)
        );
      }),
      map(firebaseUser => {
        const firebaseData = this.mapFirebaseUserToFirebaseUserData(firebaseUser);
        if (!firebaseData) {
          throw new Error('Errore nel recupero dati utente');
        }
        return firebaseData;
      }),
      catchError(error => {
        const errorMessage = this.getErrorMessage(error);
        this.errorSubject.next(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Logout
   */
  logout(): Observable<void> {
    return from(signOut(this.auth)).pipe(
      tap(() => this.errorSubject.next(null)),
      catchError(error => {
        const errorMessage = this.getErrorMessage(error);
        this.errorSubject.next(errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Ottiene il token ID corrente
   */
  getIdToken(forceRefresh: boolean = false): Observable<string | null> {
    const currentUser = this.auth.currentUser;
    if (!currentUser) {
      return of(null);
    }
    return from(currentUser.getIdToken(forceRefresh));
  }

  /**
   * Pulisce l'errore corrente
   */
  clearError(): void {
    this.errorSubject.next(null);
  }

  /**
   * Allinea il displayName su Firebase (es. dopo completamento profilo Google).
   */
  updateGoogleDisplayName(name: string, surname: string): Observable<void> {
    const cu = this.auth.currentUser;
    if (!cu) {
      return of(undefined);
    }
    const displayName = `${name} ${surname}`.trim();
    return from(updateProfile(cu, { displayName }));
  }

  /**
   * Converte FirebaseUserData in CreateUserDto
   * Parsa il displayName per estrarre nome e cognome
   * Garantisce che tutti i campi obbligatori abbiano sempre valori di default validi
   * Gestisce tutti i casi in cui i dati Google possono mancare
   */
  createUserDtoFromFirebase(firebaseUser: FirebaseUserData): CreateUserDto {
    // Validazione critica: firebaseUid è obbligatorio
    if (!firebaseUser.uid || firebaseUser.uid.trim() === '') {
      throw new Error('Firebase UID mancante: impossibile creare l\'utente');
    }

    const uid = firebaseUser.uid.trim();
    let name = 'Utente';
    let surname = 'Google';

    // Prova a parsare il displayName
    if (firebaseUser.displayName && typeof firebaseUser.displayName === 'string') {
      const trimmedDisplayName = firebaseUser.displayName.trim();
      if (trimmedDisplayName.length > 0) {
        const parts = trimmedDisplayName.split(/\s+/).filter(part => part.length > 0);
        if (parts.length >= 2) {
          // Due o più parti: prima parte è nome, resto è cognome
          name = parts[0];
          surname = parts.slice(1).join(' '); // Gestisce cognomi multipli
        } else if (parts.length === 1 && parts[0]) {
          // Una sola parte: usala come nome, mantieni default per cognome
          name = parts[0];
          surname = 'Google';
        }
      }
    }
    // Se displayName è null/undefined/vuoto/solo spazi, usa i valori di default già impostati

    // Validazione finale: assicurati che name e surname non siano mai vuoti
    name = (name && name.trim()) ? name.trim() : 'Utente';
    surname = (surname && surname.trim()) ? surname.trim() : 'Google';

    // Gestione email: se mancante, usa un fallback basato sull'UID
    let email = '';
    if (firebaseUser.email && typeof firebaseUser.email === 'string' && firebaseUser.email.trim().length > 0) {
      email = firebaseUser.email.trim();
    } else {
      // Fallback: genera un'email temporanea basata sull'UID
      // Rimuovi caratteri non validi per email dall'UID
      let sanitizedUid = uid.replace(/[^a-zA-Z0-9]/g, '').substring(0, 20);
      // Se dopo la sanitizzazione l'UID è vuoto, usa un hash dell'UID originale
      if (!sanitizedUid || sanitizedUid.length === 0) {
        // Crea un hash semplice dall'UID originale (primi 20 caratteri)
        sanitizedUid = uid.replace(/[^a-zA-Z0-9]/g, '_').substring(0, 20) || 'user';
      }
      email = `user_${sanitizedUid}@google.temp`;
    }

    // Validazione finale email: deve essere una stringa non vuota
    if (!email || email.trim() === '') {
      // Ultimo fallback se tutto fallisce: usa primi caratteri dell'UID
      const fallbackId = uid.substring(0, 20).replace(/[^a-zA-Z0-9]/g, '_') || 'user';
      email = `user_${fallbackId}@google.temp`;
    }

    return {
      name: name,
      surname: surname,
      email: email,
      firebaseUid: uid
    };
  }

  /**
   * Mappa un FirebaseUser al nostro modello FirebaseUserData
   */
  private mapFirebaseUserToFirebaseUserData(firebaseUser: FirebaseUser | null): FirebaseUserData | null {
    if (!firebaseUser) {
      return null;
    }

    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email,
      displayName: firebaseUser.displayName,
      photoURL: firebaseUser.photoURL,
      emailVerified: firebaseUser.emailVerified
    };
  }

  /**
   * Gestisce i messaggi di errore Firebase
   */
  private getErrorMessage(error: any): string {
    if (error?.code) {
      switch (error.code) {
        case 'auth/user-not-found':
          return 'Utente non trovato';
        case 'auth/wrong-password':
          return 'Password non corretta';
        case 'auth/invalid-credential':
          return 'Credenziali non valide';
        case 'auth/email-already-in-use':
          return 'Email già in uso';
        case 'auth/weak-password':
          return 'Password troppo debole (minimo 6 caratteri)';
        case 'auth/invalid-email':
          return 'Email non valida';
        case 'auth/too-many-requests':
          return 'Troppi tentativi. Riprova più tardi';
        case 'auth/network-request-failed':
          return 'Errore di rete. Controlla la connessione';
        case 'auth/user-disabled':
          return 'Account disabilitato';
        case 'auth/operation-not-allowed':
          return 'Operazione non consentita';
        case 'auth/popup-closed-by-user':
          return 'Accesso annullato dall\'utente';
        case 'auth/popup-blocked':
          return 'Popup bloccato dal browser. Abilita i popup per questo sito';
        case 'auth/account-exists-with-different-credential':
          return 'Esiste già un account con questa email ma con un metodo di accesso diverso';
        case 'auth/cancelled-popup-request':
          return 'Richiesta popup annullata';
        default:
          return error.message || 'Errore sconosciuto';
      }
    }
    return error?.message || 'Errore sconosciuto';
  }
}
